import { z } from 'zod';

// ============== EPI SCHEMAS ==============

export const epiTypeFormSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido').max(100, 'Máximo 100 caracteres'),
  code: z.string().max(50, 'Máximo 50 caracteres').optional().or(z.literal('')),
  category: z.enum(['head', 'hands', 'eyes', 'feet', 'body', 'respiratory', 'hearing']).optional(),
  description: z.string().max(1000, 'Máximo 1000 caracteres').optional().or(z.literal('')),
  lifespan_months: z.number().int('Debe ser un número entero').min(1, 'Mínimo 1 mes').optional().nullable(),
  replacement_alert_days: z.number().int('Debe ser un número entero').min(1, 'Mínimo 1 día').default(30),
  requires_certification: z.boolean().default(false),
  image_url: z.string().url('URL inválida').optional().or(z.literal('')),
  is_active: z.boolean().default(true),
});

export const epiInventoryFormSchema = z.object({
  epi_type_id: z.string().uuid('Seleccione un tipo de EPI'),
  serial_number: z.string().max(100, 'Máximo 100 caracteres').optional().or(z.literal('')),
  batch_number: z.string().max(100, 'Máximo 100 caracteres').optional().or(z.literal('')),
  purchase_date: z.string().optional().or(z.literal('')),
  expiry_date: z.string().optional().or(z.literal('')),
  status: z.enum(['available', 'assigned', 'expired', 'damaged', 'disposed']).default('available'),
  supplier: z.string().max(255, 'Máximo 255 caracteres').optional().or(z.literal('')),
  cost: z.number().min(0, 'El coste no puede ser negativo').optional().nullable(),
  notes: z.string().max(1000, 'Máximo 1000 caracteres').optional().or(z.literal('')),
});

export const epiAssignmentFormSchema = z.object({
  epi_inventory_id: z.string().uuid('Seleccione un EPI'),
  employee_id: z.string().uuid('Seleccione un empleado'),
  expected_return_date: z.string().optional().or(z.literal('')),
  notes: z.string().max(1000, 'Máximo 1000 caracteres').optional().or(z.literal('')),
});

export const epiReturnFormSchema = z.object({
  condition_on_return: z.enum(['good', 'worn', 'damaged']),
  notes: z.string().max(1000, 'Máximo 1000 caracteres').optional().or(z.literal('')),
});

// ============== INCIDENT SCHEMAS ==============

export const incidentTypeFormSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido').max(100, 'Máximo 100 caracteres'),
  code: z.string().max(50, 'Máximo 50 caracteres').optional().or(z.literal('')),
  severity_default: z.enum(['minor', 'moderate', 'serious', 'critical']).default('minor'),
  requires_investigation: z.boolean().default(false),
  requires_medical_attention: z.boolean().default(false),
  is_active: z.boolean().default(true),
});

export const incidentFormSchema = z.object({
  incident_type_id: z.string().uuid().optional().or(z.literal('')),
  title: z.string().min(1, 'El título es requerido').max(255, 'Máximo 255 caracteres'),
  description: z.string().min(1, 'La descripción es requerida'),
  severity: z.enum(['minor', 'moderate', 'serious', 'critical']).default('minor'),
  category: z.string().max(50, 'Máximo 50 caracteres').optional().or(z.literal('')),
  occurred_at: z.string().min(1, 'La fecha del incidente es requerida'),
  location: z.string().max(255, 'Máximo 255 caracteres').optional().or(z.literal('')),
  area: z.string().max(100, 'Máximo 100 caracteres').optional().or(z.literal('')),
  victim_id: z.string().uuid().optional().or(z.literal('')),
  immediate_actions: z.string().optional().or(z.literal('')),
  injury_type: z.string().max(100, 'Máximo 100 caracteres').optional().or(z.literal('')),
  days_lost: z.number().int('Debe ser un número entero').min(0, 'No puede ser negativo').default(0),
  medical_attention: z.boolean().default(false),
  hospitalization: z.boolean().default(false),
  work_order_id: z.string().uuid().optional().or(z.literal('')),
  machine_id: z.string().uuid().optional().or(z.literal('')),
  notes: z.string().optional().or(z.literal('')),
});

export const incidentInvestigationSchema = z.object({
  root_cause: z.string().optional().or(z.literal('')),
  corrective_actions: z.string().optional().or(z.literal('')),
  preventive_actions: z.string().optional().or(z.literal('')),
});

export const incidentCloseSchema = z.object({
  root_cause: z.string().min(1, 'La causa raíz es requerida para cerrar'),
  corrective_actions: z.string().min(1, 'Las acciones correctivas son requeridas'),
  preventive_actions: z.string().optional().or(z.literal('')),
  notes: z.string().optional().or(z.literal('')),
});

// ============== TRAINING SCHEMAS ==============

export const trainingTypeFormSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido').max(200, 'Máximo 200 caracteres'),
  code: z.string().max(50, 'Máximo 50 caracteres').optional().or(z.literal('')),
  description: z.string().max(1000, 'Máximo 1000 caracteres').optional().or(z.literal('')),
  duration_hours: z.number().int('Debe ser un número entero').min(1, 'Mínimo 1 hora').optional().nullable(),
  validity_months: z.number().int('Debe ser un número entero').min(1, 'Mínimo 1 mes').optional().nullable(),
  is_mandatory: z.boolean().default(false),
  required_for_roles: z.array(z.string()).optional(),
  is_active: z.boolean().default(true),
});

export const trainingSessionFormSchema = z.object({
  training_type_id: z.string().uuid('Seleccione un tipo de formación'),
  title: z.string().min(1, 'El título es requerido').max(255, 'Máximo 255 caracteres'),
  description: z.string().max(1000, 'Máximo 1000 caracteres').optional().or(z.literal('')),
  instructor: z.string().max(255, 'Máximo 255 caracteres').optional().or(z.literal('')),
  location: z.string().max(255, 'Máximo 255 caracteres').optional().or(z.literal('')),
  scheduled_date: z.string().min(1, 'La fecha es requerida'),
  start_time: z.string().optional().or(z.literal('')),
  end_time: z.string().optional().or(z.literal('')),
  max_participants: z.number().int('Debe ser un número entero').min(1, 'Mínimo 1 participante').optional().nullable(),
  status: z.enum(['scheduled', 'in_progress', 'completed', 'cancelled']).default('scheduled'),
  materials_url: z.string().url('URL inválida').optional().or(z.literal('')),
  notes: z.string().max(1000, 'Máximo 1000 caracteres').optional().or(z.literal('')),
});

export const trainingAttendanceSchema = z.object({
  attendance_status: z.enum(['registered', 'attended', 'absent', 'excused']),
  passed: z.boolean().optional().nullable(),
  score: z.number().min(0, 'Mínimo 0').max(100, 'Máximo 100').optional().nullable(),
  certificate_issued: z.boolean().default(false),
  certificate_expiry: z.string().optional().or(z.literal('')),
  notes: z.string().max(500, 'Máximo 500 caracteres').optional().or(z.literal('')),
});

// ============== TYPE EXPORTS ==============

export type EPITypeFormData = z.infer<typeof epiTypeFormSchema>;
export type EPIInventoryFormData = z.infer<typeof epiInventoryFormSchema>;
export type EPIAssignmentFormData = z.infer<typeof epiAssignmentFormSchema>;
export type EPIReturnFormData = z.infer<typeof epiReturnFormSchema>;
export type IncidentTypeFormData = z.infer<typeof incidentTypeFormSchema>;
export type IncidentFormData = z.infer<typeof incidentFormSchema>;
export type IncidentInvestigationData = z.infer<typeof incidentInvestigationSchema>;
export type IncidentCloseData = z.infer<typeof incidentCloseSchema>;
export type TrainingTypeFormData = z.infer<typeof trainingTypeFormSchema>;
export type TrainingSessionFormData = z.infer<typeof trainingSessionFormSchema>;
export type TrainingAttendanceData = z.infer<typeof trainingAttendanceSchema>;
